import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { RequestStatus } from "@fitness-app/data-models";
import {
  LiveWorkoutStatus,
  WorkoutSliderType,
  type LiveWorkoutData,
  type Timer,
  type WorkoutSliderItemType,
} from "@fitness-app/data-models/entities/LiveWorkout";
import { type WorkoutActivityEvent } from "@fitness-app/data-models/entities/ProgramActivity";

import { fetchSelectedActivity } from "./actions/fetchSelectedActivity";
import { saveLiveWorkoutToSpecifiedDay } from "./actions/saveLiveWorkoutToSpecifiedDay";
import { LIVE_WORKOUT_REDUCER_NAME, type LiveWorkoutReducer, type StartWorkoutPayload } from "./types";

// The timer is anchored to a wall-clock `endsAt` (TRE-1888): `secondsLeft` is only a
// DERIVED render value, so a frozen JS thread (Android foreign Activity, backgrounding,
// video fullscreen) costs display staleness, never elapsed time. Reducers stay pure —
// every time-dependent action carries `now` (epoch ms) in its payload.
const deriveSecondsLeft = (endsAt: number, now: number) => Math.max(0, Math.ceil((endsAt - now) / 1000));

const initialState: LiveWorkoutReducer = {
  isOpenModal: false,
  status: LiveWorkoutStatus.INACTIVE,
  step: 0,
  totalSteps: null,
  stepType: WorkoutSliderType.StartInfo,
  workoutDate: null,
  workoutId: null,
  stepText: "",
  timer: {
    secondsLeft: 0,
    secondsTotal: 0,
    active: false,
    unit: 15,
    endsAt: null,
  },
  workoutData: {
    totalExercises: 0,
    currentExercise: 0,
    doneExercises: 0,
  },
  liveWorkout: null,
  activity: null,
  activityStatus: null,
};

const reducerSlice = createSlice({
  initialState,
  name: LIVE_WORKOUT_REDUCER_NAME,
  reducers: {
    startWorkout(state, action: PayloadAction<StartWorkoutPayload>) {
      state.isOpenModal = true;
      state.workoutDate = action.payload.workoutDate;
      state.stepType = action.payload.stepType;
      state.totalSteps = action.payload.totalSteps;
      state.stepText = action.payload.stepText;
      state.status = LiveWorkoutStatus.ACTIVE;
      state.step = action.payload.step ?? 0;
      state.workoutData = {
        ...state.workoutData,
        totalExercises: action.payload.totalExercises,
        currentExercise: action.payload.currentExercise ?? 1,
        doneExercises: action.payload.doneExercises ?? 0,
      };
      state.liveWorkout = action.payload.workout;
    },
    closeLiveWorkoutSheet(state) {
      state.isOpenModal = false;
    },
    openLiveWorkoutSheet(state) {
      state.isOpenModal = true;
    },
    changeStep(
      state,
      action: PayloadAction<{ step: number; stepText?: string | null; stepType: WorkoutSliderItemType }>,
    ) {
      state.step = action.payload.step;
      state.stepText = action.payload.stepText || "";
      state.stepType = action.payload.stepType;
    },
    setTimerStep(state, action: PayloadAction<{ step: number; stepType: WorkoutSliderItemType; restTime: number }>) {
      state.step = action.payload.step;
      state.stepType = action.payload.stepType;
      state.timer = {
        ...state.timer,
        secondsTotal: action.payload.restTime,
        secondsLeft: action.payload.restTime,
        // A new step is never armed here — timerStart anchors it. If a stale interval is
        // still running (active without an anchor), liveWorkoutTimerSync normalizes it to
        // a stop instead of silently counting the new rest down.
        endsAt: null,
      };
    },
    setTimer(state, action: PayloadAction<Timer>) {
      state.timer = {
        ...state.timer,
        ...action.payload,
      };
    },
    timerStart(state, { payload: now }: PayloadAction<number>) {
      state.timer.active = true;
      state.timer.endsAt = now + state.timer.secondsLeft * 1000;
    },
    timerStop(state, { payload: now }: PayloadAction<number>) {
      if (state.timer.endsAt !== null) {
        state.timer.secondsLeft = deriveSecondsLeft(state.timer.endsAt, now);
      }
      state.timer.active = false;
      state.timer.endsAt = null;
    },
    timerSync(state, { payload: now }: PayloadAction<number>) {
      if (state.timer.endsAt !== null) {
        state.timer.secondsLeft = deriveSecondsLeft(state.timer.endsAt, now);
      }
    },
    timerReset(state) {
      state.timer = initialState.timer;
    },
    changeTimerDuration(state, { payload }: PayloadAction<{ delta: number; now: number }>) {
      const { delta, now } = payload;
      const previousTotal = state.timer.secondsTotal;
      state.timer.secondsTotal = Math.max(0, previousTotal + delta);
      // Shift the anchor by the delta that actually landed on secondsTotal, not the raw
      // one — a -15 clamped at zero followed by +15 would otherwise restore the ORIGINAL
      // deadline while secondsTotal says 15, and the dial diverges from the anchor.
      const appliedDelta = state.timer.secondsTotal - previousTotal;
      if (state.timer.endsAt !== null) {
        state.timer.endsAt += appliedDelta * 1000;
        state.timer.secondsLeft = deriveSecondsLeft(state.timer.endsAt, now);
      } else {
        state.timer.secondsLeft = Math.max(0, state.timer.secondsLeft + appliedDelta);
      }
    },
    markExerciseAsDone(
      state,
      { payload }: PayloadAction<undefined | { currentExercise: number; doneExercises: number }>,
    ) {
      state.workoutData = {
        ...state.workoutData,
        currentExercise: payload?.currentExercise ?? state.workoutData.currentExercise + 1,
        doneExercises: payload?.doneExercises ?? state.workoutData.doneExercises + 1,
      };
    },
    cancelWorkout() {
      return {
        ...initialState,
      };
    },
    endWorkout() {
      return {
        ...initialState,
      };
    },
    updateLiveWorkoutActivity(state, { payload }: PayloadAction<WorkoutActivityEvent>) {
      if (payload.id === state.activity?.id) {
        state.activity = payload;
      }
    },
    updateLiveWorkout(state, { payload }: PayloadAction<LiveWorkoutData>) {
      state.liveWorkout = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchSelectedActivity.pending, (state) => {
      state.activityStatus = RequestStatus.FETCHING;
      if (state.status === LiveWorkoutStatus.ACTIVE) {
        // Peeking at another day mid-workout refetches the selected activity; nulling
        // `activity` here dropped the live sheet into its "step not found" placeholder
        // and discarded the sets being typed (TRE-1888).
        return;
      }
      state.activity = null;
      state.liveWorkout = null;
      state.step = 0;
      state.totalSteps = null;
      state.stepType = WorkoutSliderType.StartInfo;
      state.stepText = "";
      state.timer = initialState.timer;
    });
    builder.addCase(fetchSelectedActivity.fulfilled, (state, { payload }) => {
      state.activity = payload;
      state.activityStatus = RequestStatus.SUCCESS;
      state.workoutId = payload.workoutId;
      state.workoutDate = payload.eventDate;
    });
    builder.addCase(fetchSelectedActivity.rejected, (state) => {
      state.activityStatus = RequestStatus.FAILED;
    });
    builder.addCase(saveLiveWorkoutToSpecifiedDay.fulfilled, (state, { payload }) => {
      if (state.activity) {
        state.activity = {
          ...state.activity,
          ...payload,
        };
      }
    });
  },
});

export const {
  closeLiveWorkoutSheet,
  startWorkout,
  openLiveWorkoutSheet,
  endWorkout,
  cancelWorkout,
  markExerciseAsDone,
  timerStart,
  setTimerStep,
  changeStep,
  changeTimerDuration,
  timerStop,
  timerSync,
  timerReset,
  updateLiveWorkoutActivity,
  updateLiveWorkout,
  setTimer,
} = reducerSlice.actions;

export default reducerSlice.reducer;
